import { rankingPorUtm, type FiltroDeCampo, type ItemDoFunilNoCrm } from "@base/contracts";
import { CabecalhoDePagina, EsqueletoDeCartao, EstadoDeErro, toast } from "@base/ui";
import { startOfDay } from "date-fns";
import { FlaskConical, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { Link } from "../../../shared/navegacao/navegacao";
import { PERIODO_PADRAO, periodoPronto, type Intervalo } from "../../../shared/periodo/periodos";
import { SeletorDePeriodo } from "../../../shared/periodo/seletor-de-periodo";
import { useConexaoKommo } from "../../conexoes/application/conexoes.contexto";
import {
  useHistoricoDeContatosDoDashboard,
  useOportunidadesDoDashboard,
  useOpcoesDeFiltro,
  useResumoDoDashboard,
} from "../application/dashboard.contexto";
import { FiltrosAtivos, FiltrosDoDashboard } from "./filtros-do-dashboard";
import { calcularIndicadores, divisaoDoInvestimento, type MetasNoPainel } from "../domain/indicadores";
import { formatar } from "../domain/formatadores";
import { FunilDeConversao } from "./funil-de-conversao";
import { GrupoDeIndicadores } from "./grupo-de-indicadores";
import { HistoricoDeContatos } from "./historico-de-contatos";
import { ListaDeOportunidades } from "./lista-de-oportunidades";

/** Dashboard do projeto: periodo no topo, resultado financeiro e funil. */
export function PaginaDeDashboard({
  clienteId,
  cliente,
  nomeDoUsuario,
  acaoDeSincronizar = null,
  usarMeta,
  aoPlanejar,
}: {
  readonly clienteId: string;
  readonly cliente: string;
  readonly nomeDoUsuario: string;
  /** Botao de sincronizar o CRM, vindo de quem compoe a tela (so para quem configura). */
  readonly acaoDeSincronizar?: ReactNode;
  /** Hook das metas do periodo (Funil Projetado), injetado por quem compoe a tela. */
  readonly usarMeta?: (clienteId: string, inicio: Date, fim: Date) => MetasNoPainel;
  /** Leva ao Funil Projetado (so para quem pode planejar); vira o botao do aviso. */
  readonly aoPlanejar?: () => void;
}) {
  const [periodo, setPeriodo] = useState<Intervalo>(() =>
    periodoPronto(PERIODO_PADRAO, startOfDay(new Date())),
  );
  const [filtros, setFiltros] = useState<FiltroDeCampo[]>([]);
  const [etapa, setEtapa] = useState<ItemDoFunilNoCrm>("crm");
  const consulta = useResumoDoDashboard(clienteId, periodo, filtros);
  const oportunidades = useOportunidadesDoDashboard(clienteId, periodo, filtros, etapa, consulta.data !== undefined);
  const historico = useHistoricoDeContatosDoDashboard(clienteId, periodo, filtros, consulta.data !== undefined);
  // Sempre o mesmo hook (ou nenhum) durante a vida da tela: regra dos hooks respeitada.
  const painelDeMetas = (usarMeta ?? semMetas)(clienteId, periodo.inicio, periodo.fim);
  const metas = painelDeMetas.meta;
  const dados = consulta.data?.resumo;
  const ehExemplo = consulta.data?.ehExemplo ?? false;
  const kommo = useConexaoKommo(clienteId);
  const recorte = kommo.data?.recorte ?? null;
  const textoDoRecorte =
    recorte === null
      ? null
      : recorte.grupos
          .map((grupo) => {
            const texto = grupo.condicoes
              .map((condicao) => {
                const nome =
                  kommo.data?.camposPersonalizados.find((campo) => campo.id === condicao.campoId)?.nome ?? "Campo";
                return `${nome} é ${condicao.valor}`;
              })
              .join(grupo.operador === "ou" ? " ou " : " e ");
            return recorte.grupos.length > 1 ? `(${texto})` : texto;
          })
          .join(recorte.operador === "ou" ? " ou " : " e ");
  // Meta so faz sentido com numeros reais e sem filtro (a meta e da clinica toda).
  const metasVisiveis = !ehExemplo && filtros.length === 0 && recorte === null ? metas : null;
  useAvisoDeMesSemPlanejamento({
    ativo: Boolean(usarMeta) && consulta.data !== undefined && !ehExemplo && !painelDeMetas.carregando,
    meses: painelDeMetas.mesesSemPlanejamento,
    aoPlanejar,
  });
  // Filtros so existem com o Kommo conectado (dados reais).
  const comKommo = consulta.data !== undefined && !ehExemplo;
  const opcoes = useOpcoesDeFiltro(clienteId, comKommo);
  const campos = opcoes.data ?? [];
  const indicadores = useMemo(
    () => (dados ? calcularIndicadores(dados.atual, dados.anterior) : []),
    [dados],
  );
  const primeiroNome = nomeDoUsuario.split(" ")[0] ?? nomeDoUsuario;

  return (
    <div className="flex flex-col gap-secao">
      <CabecalhoDePagina
        rotulo={cliente}
        titulo="Dashboard"
        descricao={`Olá, ${primeiroNome}. Do anúncio ao tratamento fechado, no período escolhido.`}
        acoes={
          <div className="flex flex-wrap items-center gap-2">
            {comKommo ? acaoDeSincronizar : null}
            {comKommo ? (
              <FiltrosDoDashboard
                campos={campos}
                carregando={opcoes.isPending}
                valor={filtros}
                aoMudar={setFiltros}
              />
            ) : null}
            <SeletorDePeriodo valor={periodo} aoMudar={setPeriodo} />
          </div>
        }
      />

      {comKommo && recorte ? (
        <p className="text-sm text-muted-foreground">
          Este projeto só conta leads com {textoDoRecorte}.
        </p>
      ) : null}

      {comKommo ? <FiltrosAtivos campos={campos} valor={filtros} aoMudar={setFiltros} /> : null}

      {ehExemplo ? (
        <p
          role="note"
          className="-mt-6 flex items-start gap-3 rounded-2xl bg-warning/10 px-5 py-4 text-sm leading-relaxed text-foreground"
        >
          <FlaskConical className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          <span>
            <strong className="font-semibold">Dados de exemplo.</strong> Os números são ilustrativos
            até o Kommo do projeto ser conectado em{" "}
            <Link para="/configuracoes/conexoes" className="font-semibold underline underline-offset-4">
              Configurações › Conexões
            </Link>
            .
          </span>
        </p>
      ) : dados?.atualizadoEm ? (
        <p className="-mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className="size-3.5" aria-hidden="true" />
          Dados do Kommo atualizados em {formatar.momento(dados.atualizadoEm)}
        </p>
      ) : null}

      {consulta.isPending ? (
        <div className="grid gap-bloco sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
          {Array.from({ length: 6 }, (_, indice) => (
            <EsqueletoDeCartao key={indice} rotulo="Carregando indicadores" />
          ))}
        </div>
      ) : consulta.isError || !dados ? (
        <EstadoDeErro mensagem={mensagemDoErro(consulta.error)} aoTentarNovamente={() => void consulta.refetch()} />
      ) : (
        <div
          className={`flex flex-col gap-secao transition-opacity ${consulta.isPlaceholderData ? "opacity-60" : ""}`}
          aria-busy={consulta.isPlaceholderData}
        >
          <GrupoDeIndicadores
            indicadores={indicadores}
            metas={metasVisiveis}
            investimentoPorPlataforma={divisaoDoInvestimento(dados.atual)}
          />
          <FunilDeConversao
            funil={dados.funil}
            investimentoCentavos={dados.atual.investimentoCentavos}
            operacao={dados.operacao}
            metas={metasVisiveis}
            etapaAtiva={etapa}
            aoEscolherEtapa={setEtapa}
          />
          <ListaDeOportunidades
            etapa={etapa}
            aoMudarEtapa={setEtapa}
            dados={oportunidades.data?.dados ?? []}
            ranking={
              oportunidades.data && oportunidades.data.dados.length === oportunidades.data.total
                ? rankingPorUtm(oportunidades.data.dados)
                : (oportunidades.data?.rankingUtm ?? [])
            }
            total={oportunidades.data?.total ?? 0}
            carregando={oportunidades.isPending}
            periodo={periodo}
          />
          <HistoricoDeContatos
            dados={historico.data?.dados ?? []}
            total={historico.data?.total ?? 0}
            carregando={historico.isPending}
            periodo={periodo}
          />
        </div>
      )}
    </div>
  );
}

const semMetas = (): MetasNoPainel => ({ meta: null, mesesSemPlanejamento: [], carregando: false });

/**
 * Toast de aviso quando o periodo tem mes sem Funil Projetado: sem meta, o
 * Dashboard mostra so o realizado. Um aviso por combinacao de meses, para nao
 * repetir a cada troca de filtro ou atualizacao.
 */
function useAvisoDeMesSemPlanejamento({
  ativo,
  meses,
  aoPlanejar,
}: {
  readonly ativo: boolean;
  readonly meses: readonly string[];
  readonly aoPlanejar: (() => void) | undefined;
}) {
  const chave = meses.join(",");
  useEffect(() => {
    if (!ativo || chave === "") return;
    const lista = chave.split(",");
    toast.warning(
      lista.length === 1 ? `${capitalizar(lista[0] ?? "")} sem Funil Projetado` : "Meses sem Funil Projetado",
      {
        id: `mes-sem-planejamento:${chave}`,
        description: `${lista.length === 1 ? "Esse mês ainda não tem" : `${lista.join(", ")} ainda não têm`} metas planejadas. O Dashboard mostra só o realizado, sem Meta | Real.`,
        duration: 10_000,
        ...(aoPlanejar ? { action: { label: "Planejar", onClick: aoPlanejar } } : {}),
      },
    );
  }, [ativo, chave, aoPlanejar]);
}

const capitalizar = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);
